// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { KeyCode } from '@shared/commons/KeyEventProcessor';
import { MathUtils } from '@shared/utils/MathUtils';
import { UpDownType } from '@shared/utils/Instruments/Intervals';
import { popupErrorHandler, terceraNumericHandler } from '@shared/utils/AppHandlers';
import TerceraNumericTemplate from '@front/templates/TerceraNumericTemplate.html';
import { Control, ControlEvents } from './Control';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { ScrollUtils } from '../UtilsClasses/ScrollUtils';
import { ControlsTypes } from '../UtilsClasses/FactoryConstants';
import { TradingNumericErrorChecker } from '@shared/commons/Trading/TradingNumericErrorChecker';
import { ControlsUtils } from '../UtilsClasses/ControlsUtils';
import { NumericUtils } from '@shared/utils/NumericUtils';
// TODO. Refactor.
export class TerceraNumeric extends Control {
    constructor() {
        super();
        this.currentMoveType = null;
    }
    getType() {
        return ControlsTypes.TerceraNumeric;
    }
    oninit() {
        super.oninit();
        this.updateRegExp(this.get('allowMinus'));
        this.currentMoveType = UpDownType.None;
        this.on('focus', this.private_Focus);
        this.on('mousewheel', this.private_MouseWheel);
        this.on('keyDown', this.private_KeyDown);
        this.on('keyUp', this.private_KeyUp);
        this.on(ControlEvents.LostFocus, this.onLostFocus);
        this.observe('visible', this.onVisibleChanged);
        // ! надо сохранить числовое значение и текстовое паралельно !
        this.observe('value', this.updateNumeric);
        this.observe('formattedValue', this.onFormattedValueChanged, { init: false });
        this.observe('minValue maxValue customError', this.checkErrors, { init: false });
        this.observe('decimalPrecision', this.onDecimalPrecisionChanged, { init: false });
        this.observe('allowMinus', () => {
            this.updateRegExp(this.get('allowMinus'));
        }, { init: false });
        this.observe('enabled errorText', this.onNeedUpdateValidationStateInfo, { init: false });
        this.observe('hasError', function (newValue) {
            TerceraNumeric.OnHasErrorsChanged.Raise(this, newValue);
        }, { init: false });
        this.observe('DynPropertyData', this.onDynPropertyDataChanged, { init: false });
        this.setInitialValue();
    }
    get decimalPrecision() {
        const intervals = this.get('Intervals');
        if (isValidArray(intervals)) {
            const interval = NumericUtils.getInterval(intervals, this.get('value'));
            return interval.DecimalPlaces;
        }
        return this.get('decimalPrecision');
    }
    setInitialValue() {
        var _a;
        const initialValue = (_a = this.getValue()) !== null && _a !== void 0 ? _a : this.get('defaultValue');
        void this.set('value', initialValue);
    }
    onDynPropertyDataChanged(newValue, oldValue) {
        if (isNullOrUndefined(newValue)) {
            return;
        }
        void this.set({
            value: newValue.value,
            min: newValue.minimalValue,
            max: newValue.maximalValue,
            step: newValue.increment,
            numericPrecision: newValue.decimalPlaces,
            validationStateInfo: 1
        });
    }
    arrowMouseDownWrapper(context, isUp) {
        const event = context.event;
        event === null || event === void 0 ? void 0 : event.preventDefault();
        this.private_arrowMousedown(this, isUp);
    }
    private_arrowMousedown(sender, isUp) {
        if (!this.get('enabled'))
            return;
        this.loopValueChange(isUp, true);
    }
    private_arrowMouseup(sender, isUp) {
        if (!this.get('enabled'))
            return;
        this.loopValueChange(isUp, false);
    }
    private_arrowMouseout(sender, isUp) {
        if (!this.get('enabled'))
            return;
        this.loopValueChange(isUp, false);
    }
    onMouseDown(event) {
        super.onMouseDown(event);
        this.fire('TerceraNumericClicked'); // #86098
    }
    // TODO. Ugly.
    processEnterKeyDown() {
        this.updateNumeric(this.digitValue(this.get('formattedValue')));
        if (!this.get('hasError') || !this.get('tradingNumeric')) {
            return;
        }
        const minValue = this.get('minValue');
        const maxValue = this.get('maxValue');
        const value = this.get('value');
        let newValue = value;
        if (value > maxValue) {
            newValue = maxValue;
        }
        else if (value < minValue) {
            newValue = minValue;
        }
        else {
            // TODO. Duplicate of TryParseValue.
            const interval = NumericUtils.getInterval(this.get('Intervals'), newValue);
            let step = 0;
            let stepPrecision = 0;
            if (!isNullOrUndefined(interval)) {
                step = interval.Increment;
                stepPrecision = interval.DecimalPlaces;
            }
            else {
                step = this.get('step');
                stepPrecision = MathUtils.getPrecision(step);
            }
            if (isValidNumber(step) && step !== 0 && !MathUtils.isValueMultipleToStep(newValue, step, stepPrecision)) {
                newValue = parseFloat(MathUtils.RoundToIncrement(newValue, step).toFixed(stepPrecision));
                // TODO. Ugly. For a case when minValue < step.
                if (newValue < step) {
                    newValue = step;
                }
            }
        }
        void this.set('value', newValue);
    }
    private_KeyDown(event) {
        if (!this.get('enabled'))
            return;
        if (event.original.keyCode === KeyCode.UP) {
            this.loopValueChange(true, true);
        }
        if (event.original.keyCode === KeyCode.DOWN) {
            this.loopValueChange(false, true);
        }
        if (event.original.keyCode === KeyCode.ENTER) {
            this.processEnterKeyDown();
        }
    }
    private_KeyUp(event) {
        if (!this.get('enabled'))
            return;
        if (event.original.keyCode === KeyCode.UP) {
            this.loopValueChange(true, false);
        }
        if (event.original.keyCode === KeyCode.DOWN) {
            this.loopValueChange(false, false);
        }
    }
    private_KeyPress(sender, event) {
        if (!this.get('enabled'))
            return;
        const result = this.validateNumericValue(event);
        if (!result) {
            // отменить  изменение и всплытие события если символы недопустимы!!!
            event.stopPropagation();
            event.preventDefault();
            return false;
        }
    }
    private_Focus(e) {
        if (!this.get('enabled') || this.get('focused'))
            return;
        this.gotFocus();
        if (this.get('tradingNumeric')) {
            if (this.get('hasError')) {
                this.showPopupError(this.get('errorText'));
            }
        }
        // Cross-browser hack for the different "input.select()" behaviours after focusing.
        // https://www.impressivewebs.com/input-select-correct-behaviour/
        const inputEl = e.node;
        // setTimeout(function () { inputEl.select(); }, 0);
        inputEl.select();
    }
    private_MouseWheel(context) {
        const event = context.event;
        if ((!this.get('enabled') ||
            !this.get('focused') ||
            this.find('input') !== document.activeElement) &&
            !this.get('onlyMouseWheel')) {
            return;
        }
        const isUp = ScrollUtils.IsScrollUp(event.deltaY);
        this.currentMoveType = isUp ? UpDownType.Up : UpDownType.Down;
        this.valueUpDownChangeProcess(isUp);
        return false;
    }
    setFocus(forceEnable = false) {
        super.setFocus();
        if (forceEnable) {
            void this.set('enabled', true);
        }
        if (!this.get('enabled')) {
            return;
        }
        const inputEl = this.find('input');
        if (!isNullOrUndefined(inputEl)) {
            inputEl.focus();
        }
    }
    onLostFocus() {
        if (this.isRequiredIntervalStart) {
            clearInterval(this.requiredInterval);
            this.isRequiredIntervalStart = false;
        }
        if (this.get('tradingNumeric')) {
            if (popupErrorHandler.isShowed()) {
                // #85040
                const strValue = this.get('formattedValue');
                if (strValue !== '') {
                    const step = this.get('step');
                    const minValue = this.get('minValue');
                    const maxValue = this.get('maxValue');
                    let newValue = parseFloat(strValue);
                    const stepPrecision = this.decimalPrecision;
                    if (isNaN(newValue)) {
                        // #92426
                        newValue = 0;
                    }
                    newValue = parseFloat(MathUtils.RoundToIncrement(newValue, step).toFixed(stepPrecision));
                    if (newValue < minValue) {
                        newValue = minValue;
                    }
                    if (newValue > maxValue) {
                        newValue = maxValue;
                    }
                    void this.set('value', newValue);
                    void this.set('formattedValue', newValue.toFixed(stepPrecision));
                }
            }
            popupErrorHandler.Hide(this);
            return;
        }
        this.updateNumeric(this.getValue());
    }
    updateRegExp(allowMinus) {
        let regStr = '0-9';
        if (allowMinus) {
            regStr += '\-';
        }
        this.regExp = new RegExp('[' + regStr + TerceraNumeric.decimalseparator + ']');
    }
    loopValueChange(isUp, start) {
        this.currentMoveType = isUp ? UpDownType.Up : UpDownType.Down;
        if (start) {
            if (!this.isRequiredIntervalStart) {
                this.isRequiredIntervalStart = true;
                this.requiredInterval = setInterval(this.valueUpDownChangeProcess.bind(this), 150, isUp);
            }
        }
        else {
            if (this.isRequiredIntervalStart) {
                clearInterval(this.requiredInterval);
                this.isRequiredIntervalStart = false;
            }
            return;
        }
        this.valueUpDownChangeProcess(isUp);
    }
    valueUpDownChangeProcess(isUp) {
        const oldVal = this.get('value');
        const onValueUpDownChangeProcess = this.get('valueUpDownChangeProcess');
        let res;
        if (isNullOrUndefined(onValueUpDownChangeProcess)) {
            res = NumericUtils.valueUpDownChangeProcess(isUp, oldVal, this.get('step') * this.get('incrementCoefficient'), this.get('minValue'), this.get('maxValue'));
        }
        else {
            res = onValueUpDownChangeProcess(isUp, oldVal);
        }
        void this.set({ value: res });
    }
    onteardown() {
        popupErrorHandler.Hide(this);
    }
    onNeedUpdateValidationStateInfo() {
        this.TryParseValue(this.digitValue(this.get('formattedValue')));
        void this.set('validationStateInfo', {
            enabled: this.get('enabled'),
            errorText: this.get('errorText')
        });
    }
    onVisibleChanged(newValue) {
        if (!newValue) {
            void this.set({ hasError: false, errorText: '' });
        }
        else {
            this.checkErrors();
        }
    }
    checkErrors() {
        if (!this.get('tradingNumeric')) {
            return;
        }
        const val = this.get('value');
        const fVal = this.get('formattedValue');
        if (isValidNumber(fVal) && fVal !== 0) {
            this.TryParseValue(fVal);
        }
        else {
            this.updateNumeric(val);
        }
    }
    onDecimalPrecisionChanged() {
        const value = this.getValue();
        if (isValidNumber(value)) {
            this.updateNumeric(value);
        }
    }
    updateNumeric(newValue, _oldValue) {
        if (!isValidNumber(newValue)) {
            newValue = this.get('minValue');
        }
        if (!isValidNumber(newValue)) {
            return;
        }
        const interval = NumericUtils.getInterval(this.get('Intervals'), newValue);
        if (!isNullOrUndefined(interval)) {
            const step = this.get('step');
            if (step !== interval.Increment) {
                void this.set({ step: interval.Increment });
            }
        }
        const res = this.validateValue(newValue, newValue, this.get('minValue'), this.get('maxValue'), this.decimalPrecision);
        if (this.get('tradingNumeric')) {
            this.TryParseValue(res);
        }
        const newFormatedValue = this.valueToString(res, interval);
        void this.set({ formattedValue: newFormatedValue, value: parseFloat(newFormatedValue) });
        const prop = this.get('DynPropertyData');
        if (isValidNumber(prop === null || prop === void 0 ? void 0 : prop.value) && prop.value !== 0) {
            prop.value = parseFloat(newFormatedValue);
            void this.set({ DynPropertyData: prop });
        }
        this.fire(TerceraNumericEvents.ValueChanged, this.getValue());
    }
    onFormattedValueChanged(newValue, _oldValue) {
        let isCorrect = newValue !== '-';
        if (newValue.includes(TerceraNumeric.commaseparator)) {
            // #115398
            newValue = newValue.replace(TerceraNumeric.commaseparator, TerceraNumeric.decimalseparator);
        }
        if (this.get('tradingNumeric')) {
            isCorrect = this.TryParseValue(this.digitValue(newValue));
        }
        if (!isValidString(newValue) || !isCorrect) {
            return;
        }
        this.setValue(newValue);
        this.updateNumeric(this.getValue());
    }
    // TODO. Refactor. Ugly.
    TryParseValue(inputValue) {
        const textValue = isValidNumber(inputValue) || inputValue === 0 ? inputValue.toString() : '';
        let correctValue = false;
        let errorTextForNumeric = '';
        if (textValue === '') {
            errorTextForNumeric = Resources.getResource('UserControl.TerceraNumeric.ValueIsEmpty');
        }
        else {
            const parsedValue = this.digitValue(textValue);
            if (isNaN(parsedValue) || textValue.split(TerceraNumeric.decimalseparator).length > 2) {
                errorTextForNumeric = Resources.getResource('UserControl.TerceraNumeric.ValueNotNumber');
            }
            else {
                const interval = NumericUtils.getInterval(this.get('Intervals'), parsedValue);
                let step = 0;
                let stepPrecision = 0;
                if (!isNullOrUndefined(interval)) {
                    step = interval.Increment;
                    stepPrecision = interval.DecimalPlaces;
                }
                else {
                    step = this.get('step');
                    stepPrecision = MathUtils.getPrecision(step);
                }
                const minValue = this.get('minValue');
                const maxValue = this.get('maxValue');
                const formatMin = MathUtils.formatValueWithEps(minValue);
                const formatMax = MathUtils.formatValueWithEps(maxValue);
                if (parsedValue < minValue) {
                    const key = this.get('valueLessMinLocalKey');
                    const standartKey = key === 'UserControl.TerceraNumeric.ValueLessMin';
                    errorTextForNumeric = Resources.getResource(key);
                    if (standartKey) {
                        errorTextForNumeric += formatMin;
                    }
                }
                else if (parsedValue > maxValue) {
                    // TODO think
                    const key = this.get('valueGreaterMaxLocalKey');
                    const standartKey = key === 'UserControl.TerceraNumeric.ValueGreaterMax';
                    errorTextForNumeric = Resources.getResource(key);
                    if (standartKey) {
                        errorTextForNumeric += formatMax;
                    }
                }
                else if (isValidNumber(step) &&
                    step !== 0 &&
                    !MathUtils.isValueMultipleToStep(parsedValue, step, stepPrecision) &&
                    (!this.get('SkipValidationForMaxValue') || parsedValue !== maxValue)) {
                    errorTextForNumeric =
                        Resources.getResource('UserControl.TerceraNumeric.ValueNotMultiple') +
                            MathUtils.formatValueWithEps(step);
                }
                else {
                    correctValue = true;
                }
            }
        }
        // TODO. Refactor. Ugly.
        const customError = this.get('customError');
        if (correctValue && !isNullOrUndefined(customError)) {
            errorTextForNumeric = customError.toString();
        }
        if (this.get('enabled') && this.get('visible')) {
            void this.set({
                hasError: !correctValue || customError,
                errorText: errorTextForNumeric
            });
        }
        // TODO. Ugly.
        if (correctValue && !isValidString(errorTextForNumeric)) {
            popupErrorHandler.Hide(this);
        }
        else {
            this.showPopupError(errorTextForNumeric);
        }
        return correctValue;
    }
    showPopupError(errorTextForNumeric) {
        if (!this.get('enabled') || !this.get('focused') || !isValidString(errorTextForNumeric)) {
            return;
        }
        popupErrorHandler.Show(this, errorTextForNumeric, '');
    }
    getAbsoluteRectangle() {
        return ControlsUtils.getAbsoluteRectangleForElement(this);
    }
    validateValue(oldVal, newVal, min, max, prec) {
        const tmpNewVal = parseFloat(newVal === null || newVal === void 0 ? void 0 : newVal.toString());
        const tmpMin = parseFloat(min === null || min === void 0 ? void 0 : min.toString());
        const tmpMax = parseFloat(max === null || max === void 0 ? void 0 : max.toString());
        let result = tmpNewVal;
        if (!this.get('tradingNumeric')) {
            if (min !== undefined && tmpMin > tmpNewVal) {
                result = tmpMin;
            }
            else if (max !== undefined && tmpMax < tmpNewVal) {
                result = tmpMax;
            }
        }
        if (isValidNumber(result)) {
            result = parseFloat(result.toFixed(prec));
            return result;
        }
        else {
            return oldVal;
        }
    }
    digitValue(value) {
        if (!isValidString(value))
            return null;
        return parseFloat(value);
    }
    valueToString(value, interval) {
        let prec = this.decimalPrecision;
        if (!isNullOrUndefined(interval)) {
            prec = interval.DecimalPlaces;
        }
        const floatValue = parseFloat(value.toString());
        const floatValueRound = floatValue.toFixed(prec);
        const res = floatValueRound.replace(/[.]/g, TerceraNumeric.decimalseparator);
        return res; // value.toLocaleString();
    }
    validateNumericValue(event) {
        var _a;
        const e = !isNullOrUndefined(event) ? event : window.event;
        const code = (_a = e.which) !== null && _a !== void 0 ? _a : e.keyCode;
        if (code < 32 || Boolean(e.ctrlKey) || Boolean(e.altKey)) {
            return true;
        }
        const char = String.fromCharCode(code);
        return this.regExp.test(char);
    }
    setValue(value) {
        void this.set('value', this.digitValue(value));
    }
    getValue() {
        return this.get('value');
    }
    static NumericHandlerInitialize() {
        terceraNumericHandler.OnHasErrorsChanged = TerceraNumeric.OnHasErrorsChanged;
        TradingNumericErrorChecker.subscribeToHandler();
    }
}
TerceraNumeric.NUMERIC_MAXVALUE = 99999999;
TerceraNumeric.OnHasErrorsChanged = new CustomEvent();
TerceraNumeric.decimalseparator = '.';
TerceraNumeric.commaseparator = ',';
export var TerceraNumericEvents;
(function (TerceraNumericEvents) {
    TerceraNumericEvents["ValueChanged"] = "ValueChanged";
})(TerceraNumericEvents || (TerceraNumericEvents = {}));
Control.extendWith(TerceraNumeric, {
    template: TerceraNumericTemplate,
    data: function () {
        return {
            name: 'num',
            minValue: 1, // Минимальное значение
            maxValue: 100000, // Максимальное значение
            step: 1, // Шаг изменения
            // из-за внедрения интервалов возникла необходимость ввести коэффициент,
            // с помощью которого мы будем откладывать количество интервалов (количество тиков)
            incrementCoefficient: 1,
            decimalPrecision: 0, // Количество знаков после decimalseparator
            value: 0, // реальное значение
            formattedValue: '', // видимое значения
            defaultValue: 0.000001,
            showArrows: true,
            tradingNumeric: false,
            hasError: false,
            errorText: '',
            customError: null,
            validationStateInfo: null,
            SkipValidationForMaxValue: false,
            Intervals: [],
            zIndex: 0,
            allowMinus: true,
            valueLessMinLocalKey: 'UserControl.TerceraNumeric.ValueLessMin',
            valueGreaterMaxLocalKey: 'UserControl.TerceraNumeric.ValueGreaterMax',
            DynPropertyData: {},
            onlyMouseWheel: false,
            isPosAbsolute: false,
            enableOnSelect: false,
            rightAlignedText: '',
            rightAlignedTextAdditionalClass: ''
        };
    }
});
